import { FbaTransportBoxGroup, FbaTransportBoxGroupItem, FbaTransportPlanStatus, FbaTransportV2024CreatePlanItem, FbaTransportV2024Label, FbaTransportV2024Operation, FbaTransportV2024Plan, FbaTransportV2024Shipment, FbaTransportV2024ShipmentBoxGroupEntry, FbaTransportV2024ShipmentContactInformation, FbaTransportV2024ShipmentFreightClass, FbaTransportV2024ShipmentGroup, PalletDetails, TransportDimensions } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';
import itemEligibilityFactory from './fbaTransportV2024/itemEligibility';
import itemPrepDetailsFactory from './fbaTransportV2024/itemPrepDetails';
import { ImportedFbaPlan, toBoxGroup, toBoxGroupItem, toFbaTransportV2024Plan, toImportedFbaPlan, toLabel, toOperation, toShipment, toShipmentBoxGroup } from './fbaTransportV2024/mappers';

export * from './fbaTransportV2024/mappers';

export type GetPlansQuery = operations['list-fba-transport-v2024-plans']['parameters']['query'];
export type GetPlansResponse = operations['list-fba-transport-v2024-plans']['responses']['200']['content']['application/json'];
export type ShowPlanResponse = operations['get-fba-transport-v2024-plan']['responses']['200']['content']['application/json'];
export type CreatePlanRequest = operations['create-fba-transport-v2024-plan']['requestBody']['content']['application/json'];
export type CreatePlanResponse = operations['create-fba-transport-v2024-plan']['responses']['200']['content']['application/json'];
export type UpdatePlanResponse = operations['update-fba-transport-v2024-plan']['responses']['200']['content']['application/json'];
export type ListOperationsResponse = operations['list-fba-transport-v2024-operations']['responses']['200']['content']['application/json'];
export type ShowOperationResponse = operations['get-fba-transport-v2024-operation']['responses']['200']['content']['application/json'];
export type FetchOperationUpdateResponse = operations['fetch-fba-transport-v2024-operation-update']['responses']['200']['content']['application/json'];
export type FetchPackingOptionsQuery = operations['fetch-fba-transport-v2024-packing-options']['parameters']['query'];
export type FetchPackingGroupItemsQuery = operations['fetch-fba-transport-v2024-packing-group-items']['parameters']['query'];
export type FetchDeliveryWindowOptionsQuery = operations['fetch-fba-transport-v2024-delivery-window-options']['parameters']['query'];
export type FetchTransportationOptionsQuery = operations['fetch-fba-transport-v2024-transportation-options']['parameters']['query'];
export type FetchTransportationOptionsResponse = operations['fetch-fba-transport-v2024-transportation-options']['responses']['200']['content']['application/json'];
export type GetShipmentsQuery = operations['get-fba-transport-v2024-shipment']['parameters']['query'];
export type GetShipmentsResponse = operations['get-fba-transport-v2024-shipment']['responses']['200']['content']['application/json'];
export type FetchShipmentBoxGroupsQuery = operations['list-fba-transport-v2024-shipment-boxes']['parameters']['query'];
export type FetchShipmentBoxGroupsResponse = operations['list-fba-transport-v2024-shipment-boxes']['responses']['200']['content']['application/json'];
export type FetchShipmentItemsQuery = operations['list-fba-transport-v2024-shipment-items']['parameters']['query'];
export type SetBoxesIdsResponse = operations['set-fba-transport-v2024-plan-box-ids']['responses']['202']['content']['application/json'];
export type GetLabelsQuery = operations['get-fba-transport-v2024-plan-labels']['parameters']['query'];
export type GetLabelsResponse = operations['get-fba-transport-v2024-plan-labels']['responses']['200']['content']['application/json'];
export type CancelPlanCheckerRequest = operations['check-cancel-fba-transport-v2024-plan']['requestBody']['content']['application/json'];
export type CancelPlanCheckerResponse = operations['check-cancel-fba-transport-v2024-plan']['responses']['200']['content']['application/json'];
export type CancelPlanRequest = operations['cancel-fba-transport-v2024-plan']['requestBody']['content']['application/json'];
export type CancelPlanResponse = operations['cancel-fba-transport-v2024-plan']['responses']['200']['content']['text/plain'];
export type MarkPlanAsCanceledResponse = operations['mark-fba-transport-v2024-plan-as-canceled']['responses']['200']['content']['application/json'];
export type GetPickListQuery = operations['get-fba-transport-v2024-pick-list']['parameters']['query'];
export type GetPickListResponse = operations['get-fba-transport-v2024-pick-list']['responses']['200']['content']['application/json'];
export type GetPackListQuery = operations['get-fba-transport-v2024-pack-list']['parameters']['query'];
export type GetPackListResponse = operations['get-fba-transport-v2024-pack-list']['responses']['200']['content']['application/json'];
export type ListAmazonPlansResponse = operations['list-amazon-fba-transport-v2024-plans']['responses']['200']['content']['application/json'];
export type ImportPlanResponse = operations['import-fba-transport-v2024-plan']['responses']['200']['content']['application/json'];
export type GeneratePackingOptionsRequest = operations['generate-fba-transport-v2024-packing-options']['requestBody']['content']['application/json'];
export type GeneratePackingOptionsResponse = operations['generate-fba-transport-v2024-packing-options']['responses']['200']['content']['text/plain'];
export type ConfirmPackingOptionRequest = operations['confirm-fba-transport-v2024-packing-option']['requestBody']['content']['application/json'];
export type ConfirmPackingOptionResponse = operations['confirm-fba-transport-v2024-packing-option']['responses']['200']['content']['text/plain'];
export type MarkPackingOptionAsConfirmedRequest = operations['mark-fba-transport-v2024-packing-option-as-confirmed']['requestBody']['content']['application/json'];
export type MarkPackingOptionAsConfirmedResponse = operations['mark-fba-transport-v2024-packing-option-as-confirmed']['responses']['200']['content']['application/json'];
export type GeneratePlacementOptionsRequest = operations['generate-fba-transport-v2024-placement-options']['requestBody']['content']['application/json'];
export type GeneratePlacementOptionsResponse = operations['generate-fba-transport-v2024-placement-options']['responses']['200']['content']['text/plain'];
export type GenerateDeliveryWindowOptionsRequest = operations['generate-fba-transport-v2024-delivery-window-options']['requestBody']['content']['application/json'];
export type GenerateDeliveryWindowOptionsResponse = operations['generate-fba-transport-v2024-delivery-window-options']['responses']['200']['content']['text/plain'];
export type GenerateTransportationOptionsRequest = operations['generate-fba-transport-v2024-transportation-options']['requestBody']['content']['application/json'];
export type GenerateTransportationOptionsResponse = operations['generate-fba-transport-v2024-transportation-options']['responses']['200']['content']['text/plain'];
export type SetFirstAvailabilityDateRequest = operations['set-fba-transport-v2024-shipment-first-availability-date']['requestBody']['content']['application/json'];
export type SetTransportModePreferenceRequest = operations['set-fba-transport-v2024-transport-mode-preference']['requestBody']['content']['application/json'];
export type ChangeUsing2dBarcodesRequest = operations['change-fba-transport-v2024-using-2d-barcodes']['requestBody']['content']['application/json'];
export type SetPackingInformationRequest = operations['set-fba-transport-v2024-packing-information']['requestBody']['content']['application/json'];
export type SetPackingInformationResponse = operations['set-fba-transport-v2024-packing-information']['responses']['200']['content']['text/plain'];
export type MarkPackingInformationAsConfirmedRequest = operations['add-fba-transport-v2024-carton-request-to-submission']['requestBody']['content']['application/json'];
export type MarkPackingInformationAsConfirmedResponse = operations['add-fba-transport-v2024-carton-request-to-submission']['responses']['200']['content']['application/json'];
export type ResetSubmissionsResponse = operations['reset-fba-transport-v2024-submissions']['responses']['200']['content']['application/json'];
export type SelectPlacementOptionRequest = operations['select-fba-transport-v2024-placement-option']['requestBody']['content']['application/json'];
export type ConfirmPlacementOptionRequest = operations['confirm-fba-transport-v2024-placement-option']['requestBody']['content']['application/json'];
export type ConfirmPlacementOptionResponse = operations['confirm-fba-transport-v2024-placement-option']['responses']['200']['content']['text/plain'];
export type MarkPlacementOptionAsConfirmedRequest = operations['mark-fba-transport-v2024-placement-option-as-confirmed']['requestBody']['content']['application/json'];
export type MarkPlacementOptionAsConfirmedResponse = operations['mark-fba-transport-v2024-placement-option-as-confirmed']['responses']['200']['content']['application/json'];
export type UpdateShipmentNameRequest = operations['update-fba-transport-v2024-shipment-name']['requestBody']['content']['application/json'];
export type UpdatePackingInfoKnownRequest = operations['update-fba-transport-v2024-packing-info-known']['requestBody']['content']['application/json'];
export type ConfirmDeliveryWindowOptionRequest = operations['confirm-fba-transport-v2024-delivery-window-option']['requestBody']['content']['application/json'];
export type ConfirmDeliveryWindowOptionResponse = operations['confirm-fba-transport-v2024-delivery-window-option']['responses']['200']['content']['text/plain'];
export type MarkDeliveryWindowOptionAsConfirmedRequest = operations['mark-fba-transport-v2024-delivery-window-option-as-confirmed']['requestBody']['content']['application/json'];
export type MarkDeliveryWindowOptionAsConfirmedResponse = operations['mark-fba-transport-v2024-delivery-window-option-as-confirmed']['responses']['200']['content']['application/json'];
export type ConfirmTransportationOptionRequest = operations['confirm-fba-transport-v2024-transportation-option']['requestBody']['content']['application/json'];
export type ConfirmTransportationOptionResponse = operations['confirm-fba-transport-v2024-transportation-option']['responses']['200']['content']['text/html'];
export type MarkTransportationOptionsAsConfirmedRequest = operations['mark-fba-transport-v2024-transportation-option-as-confirmed']['requestBody']['content']['application/json'];
export type MarkTransportationOptionsAsConfirmedResponse = operations['mark-fba-transport-v2024-transportation-option-as-confirmed']['responses']['200']['content']['application/json'];
export type CreateBoxGroupRequest = operations['create-fba-transport-v2024-box-group']['requestBody']['content']['application/json'];
export type CreateBoxGroupResponse = operations['create-fba-transport-v2024-box-group']['responses']['200']['content']['application/json'];
export type UpdateBoxGroupRequest = operations['update-fba-transport-v2024-box-group']['requestBody']['content']['application/json'];
export type UpdateBoxGroupResponse = operations['update-fba-transport-v2024-box-group']['responses']['200']['content']['application/json'];
export type RemoveBoxGroupResponse = operations['remove-fba-transport-v2024-box-group']['responses']['200']['content']['application/json'];
export type SetBoxQuantityForBoxGroupRequest = operations['set-fba-transport-v2024-box-quantity']['requestBody']['content']['application/json'];
export type SetBoxQuantityForBoxGroupResponse = operations['set-fba-transport-v2024-box-quantity']['responses']['200']['content']['application/json'];
export type CreateBoxGroupItemRequest = operations['create-fba-transport-v2024-box-group-item']['requestBody']['content']['application/json'];
export type CreateBoxGroupItemResponse = operations['create-fba-transport-v2024-box-group-item']['responses']['200']['content']['application/json'];
export type UpdateBoxGroupItemRequest = operations['update-fba-transport-v2024-box-group-item']['requestBody']['content']['application/json'];
export type UpdateBoxGroupItemResponse = operations['update-fba-transport-v2024-box-group-item']['responses']['200']['content']['application/json'];
export type RemoveBoxGroupItemResponse = operations['remove-fba-transport-v2024-box-group-item']['responses']['200']['content']['application/json'];
export type CreatePalletRequest = operations['create-fba-transport-v2024-pallet']['requestBody']['content']['application/json'];
export type CreatePalletResponse = operations['create-fba-transport-v2024-pallet']['responses']['200']['content']['application/json'];
export type UpdatePalletRequest = operations['update-fba-transport-v2024-pallet']['requestBody']['content']['application/json'];
export type UpdatePalletResponse = operations['update-fba-transport-v2024-pallet']['responses']['200']['content']['application/json'];
export type RemovePalletRequest = operations['remove-fba-transport-v2024-pallet']['requestBody']['content']['application/json'];
export type RemovePalletResponse = operations['remove-fba-transport-v2024-pallet']['responses']['200']['content']['application/json'];
export type SetNonPartneredLtlTrackingRequest = operations['update-fba-transport-v2024-tracking-ltl-details']['requestBody']['content']['application/json'];
export type SetNonPartneredLtlTrackingResponse = operations['update-fba-transport-v2024-tracking-ltl-details']['responses']['200']['content']['text/html'];
export type SetNonPartneredSmallParcelTrackingRequest = operations['update-fba-transport-v2024-tracking-small-details']['requestBody']['content']['application/json'];
export type SetNonPartneredSmallParcelTrackingResponse = operations['update-fba-transport-v2024-tracking-small-details']['responses']['200']['content']['text/html'];
export type UpdateContactInformationRequest = operations['update-fba-transport-v2024-contact-information']['requestBody']['content']['application/json'];
export type UpdateContactInformationResponse = operations['update-fba-transport-v2024-contact-information']['responses']['200']['content']['application/json'];
export type UpdateFreightInformationRequest = operations['update-fba-transport-v2024-freight-information']['requestBody']['content']['application/json'];
export type UpdateFreightInformationResponse = operations['update-fba-transport-v2024-freight-information']['responses']['200']['content']['application/json'];
export type RequestPackageLabelsRequest = operations['request-fba-transport-v2024-package-labels']['requestBody']['content']['application/json'];
export type RequestPackageLabelsResponse = operations['request-fba-transport-v2024-package-labels']['responses']['200']['content']['application/json'];
export type RequestAllBoxLabelsRequest = operations['request-fba-transport-v2024-box-labels']['requestBody']['content']['application/json'];
export type RequestAllBoxLabelsResponse = operations['request-fba-transport-v2024-box-labels']['responses']['200']['content']['application/json'];
export type RequestIndividualBoxLabelsRequest = operations['request-fba-transport-v2024-individual-box-labels']['requestBody']['content']['application/json'];
export type RequestIndividualBoxLabelsResponse = operations['request-fba-transport-v2024-individual-box-labels']['responses']['200']['content']['application/json'];
export type RequestPalletLabelsRequest = operations['request-fba-transport-v2024-pallet-labels']['requestBody']['content']['application/json'];
export type RequestPalletLabelsResponse = operations['request-fba-transport-v2024-pallet-labels']['responses']['200']['content']['application/json'];
export type DownloadPalletLabelsRequest = operations['download-fba-transport-v2024-pallet-labels']['requestBody']['content']['application/json'];
export type DownloadPalletLabelsResponse = operations['download-fba-transport-v2024-pallet-labels']['responses']['200']['content']['application/json'];
export type RequestBillOfLadingRequest = operations['request-fba-transport-v2024-bill-of-lading']['requestBody']['content']['application/json'];
export type RequestBillOfLadingResponse = operations['request-fba-transport-v2024-bill-of-lading']['responses']['200']['content']['application/json'];
export type ResizeToFourBySixRequest = operations['resize-fba-transport-v2024-label-to-4x6']['requestBody']['content']['application/json'];
export type ResizeToFourBySixResponse = operations['resize-fba-transport-v2024-label-to-4x6']['responses']['200']['content']['application/json'];

export interface ImportedFbaPlanItem {
    quantityShipped: string
    quantityInCase: string
    fulfillmentNetworkSku: string
    sellerSku: string
}

export interface ResponseListPlans {
    plans: ImportedFbaPlan[]
    paginationToken: string | null
}

export interface ImportFbaPlanResponse {
    inboundShipmentId: number
    outboundShipmentId: number
    fbaShipmentId: string
    fbaTransportV2024PlanId: string
}

interface FbaTransportV2024ApiReturn {
    itemEligibility: ReturnType<typeof itemEligibilityFactory>
    itemPrepDetails: ReturnType<typeof itemPrepDetailsFactory>
    getPlans: (outboundShipmentId: number) => Promise<FbaTransportV2024Plan[]>
    showPlan: (planId: string) => Promise<FbaTransportV2024Plan>
    generatePackingOptions: (planId: string) => Promise<string>
    fetchPackingOptions: (planId: string) => Promise<void>
    fetchPackingGroupItems: (planId: string, packingOptionId: string, packingGroupId: string) => Promise<void>
    confirmPackingOption: (planId: string, packingOptionId: string) => Promise<string>
    markPackingOptionAsConfirmed: (planId: string, packingOptionId: string) => Promise<void>
    createPlan: (outboundShipmentId: number, channelId: number, items: FbaTransportV2024CreatePlanItem[], destinationMarketplace: string | null) => Promise<FbaTransportV2024Plan>
    updatePlan: (planId: string) => Promise<void>
    getOperations: ReturnType<typeof fetchPaginatedDataFunctionWithMapper<ListOperationsResponse['data'][number], FbaTransportV2024Operation>>
    showOperation: (operationId: string) => Promise<FbaTransportV2024Operation>
    fetchOperationUpdate: (operationId: string) => Promise<FbaTransportV2024Operation>
    generatePlacementOptions: (planId: string) => Promise<string>
    fetchPlacementOptions: (planId: string) => Promise<void>
    resetPlacementOptions: (planId: string) => Promise<void>
    generateDeliveryWindowOptions: (planId: string, shipmentId: string) => Promise<string>
    fetchDeliveryWindowOptions: (planId: string, placementOptionId: string, shipmentId: string) => Promise<void>
    generateTransportationOptions: (planId: string, placementOptionId: string, shipments: { shipmentId: string, startDate: string }[]) => Promise<string>
    fetchTransportationOptions: (planId: string, placementOptionId: string, shipmentId: string) => Promise<void>
    createBoxGroup: (planId: string, associatedEntityId: string, dimensions: TransportDimensions) => Promise<FbaTransportBoxGroup>
    updateBoxGroup: (planId: string, boxGroupId: string, dimensions: TransportDimensions) => Promise<void>
    removeBoxGroup: (planId: string, boxGroupId: string) => Promise<void>
    setBoxQuantityForBoxGroup: (planId: string, boxGroupId: string, quantity: number) => Promise<number[]>
    createBoxGroupItem: (planId: string, boxGroupId: string, item: Pick<FbaTransportBoxGroupItem, 'planItemId' | 'quantity'>) => Promise<FbaTransportBoxGroupItem>
    updateBoxGroupItem: (planId: string, boxGroupId: string, itemId: string, quantity: number) => Promise<FbaTransportBoxGroupItem>
    removeBoxGroupItem: (planId: string, boxGroupId: string, itemId: string) => Promise<void>
    createPallet: (planId: string, placementOptionId: string, shipmentId: string, details: PalletDetails) => Promise<void>
    updatePallet: (planId: string, placementOptionId: string, shipmentId: string, palletId: string, details: PalletDetails) => Promise<void>
    removePallet: (planId: string, placementOptionId: string, shipmentId: string, palletId: string) => Promise<void>
    setNonPartneredLtlTracking: (planId: string, shipmentId: string, billOfLadingNumber: string, freightBillNumber: string[]) => Promise<string>
    setNonPartneredSmallParcelTracking: (planId: string, shipmentId: string, boxTrackingIds: { amazonBoxId: string, trackingId: string }[]) => Promise<string>
    updateContactInformation: (planId: string, placementOptionId: string, shipmentId: string, details: FbaTransportV2024ShipmentContactInformation) => Promise<void>
    updateFreightInformation: (planId: string, placementOptionId: string, shipmentId: string, details: { freightClass?: FbaTransportV2024ShipmentFreightClass; amount?: string; code?: string }) => Promise<void>
    setFirstAvailabilityDate: (planId: string, placementOptionId: string, shipmentId: string, firstAvailabilityDate: string) => Promise<void>
    setTransportModePreference: (planId: string, placementOptionId: string, shipmentId: string, transportModePreference: FbaTransportV2024ShipmentGroup) => Promise<void>
    getLabels: (planId: string, shipmentId: string) => Promise<FbaTransportV2024Label[]>
    requestPackageLabels: (planId: string, shipmentId: string) => Promise<void>
    requestAllBoxLabels: (planId: string, shipmentId: string, boxGroupId?: string | null, boxNumbers?: number[] | null, sortByInternalNumber?: boolean) => Promise<void>
    requestIndividualBoxLabels: (planId: string, shipmentId: string, boxNumbers: number[], sortByInternalNumber?: boolean) => Promise<void>
    requestPalletLabels: (planId: string, shipmentId: string, pallets: { number: number }[]) => Promise<void>
    downloadPalletLabels: (planId: string, shipmentId: string) => Promise<void>
    requestBillOfLading: (planId: string, shipmentId: string) => Promise<void>
    resizeToFourBySix: (planId: string, shipmentId: string, labelId: string, sortByInternalNumber?: boolean) => Promise<void>
    changeUsing2dBarcodes: (planId: string, using2dBarcodes: boolean) => Promise<void>
    setPackingInformation: (planId: string) => Promise<string>
    markPackingInformationAsConfirmed: (planId: string, packingGroupIds: string[]) => Promise<void>
    resetSubmissions: (planId: string) => Promise<ResetSubmissionsResponse>
    selectPlacementOption: (planId: string, placementOptionId: string) => Promise<void>
    confirmPlacementOption: (planId: string, placementOptionId: string) => Promise<string>
    markPlacementOptionAsConfirmed: (planId: string, placementOptionId: string) => Promise<MarkPlacementOptionAsConfirmedResponse>
    setBoxesIds: (planId: string) => Promise<void>
    updateShipmentName: (planId: string, shipmentId: string, name: string) => Promise<void>
    updatePackingInfoKnown: (planId: string, isPackingInfoKnown: boolean) => Promise<void>
    confirmDeliveryWindowOption: (planId: string, shipmentId: string, deliveryWindowOptionId: string) => Promise<string>
    markDeliveryWindowOptionAsConfirmed: (planId: string, shipmentId: string, deliveryWindowOptionId: string) => Promise<void>
    confirmTransportationOption: (planId: string, shipments: { shipmentId: string, transportationOptionId: string }[]) => Promise<string>
    markTransportationOptionsAsConfirmed: (planId: string, shipments: { shipmentId: string, transportationOptionId: string }[]) => Promise<void>
    getShipments: (planId: string, placementOptionId: string) => Promise<FbaTransportV2024Shipment[]>
    fetchShipmentBoxGroups: (planId: string, placementOptionId: string, shipmentId: string) => Promise<FbaTransportV2024ShipmentBoxGroupEntry[]>
    fetchShipmentItems: (planId: string, placementOptionId: string, shipmentId: string) => Promise<void>
    cancelPlanChecker: (planId: string) => Promise<string[]>
    cancelPlan: (planId: string) => Promise<string>
    markPlanAsCanceled: (planId: string) => Promise<void>
    getPickListUrl: (planId: string, placementOptionId: string, shipmentId: string) => Promise<string>
    getPackListUrl: (planId: string, placementOptionId: string, shipmentId: string) => Promise<string>
    listPlans: (warehouseId: number, status: FbaTransportPlanStatus | null, paginationToken?: string | null) => Promise<ResponseListPlans>
    importPlan: (planId: string, channelId: number, warehouseId: number, createInbound?: boolean) => Promise<FbaTransportV2024Plan>
}

export default (migratedAxios: AxiosInstance = axiosWithoutCaseMiddleware): FbaTransportV2024ApiReturn => ({
    itemEligibility: itemEligibilityFactory(migratedAxios),
    itemPrepDetails: itemPrepDetailsFactory(migratedAxios),

    getPlans: async (outboundShipmentId: number): Promise<FbaTransportV2024Plan[]> => {
        const query: GetPlansQuery = { outbound_shipment_id: outboundShipmentId };
        const response = await migratedAxios.get<GetPlansResponse>('/api/fba-transport/v2024/plans', { params: query });

        return response.data.data.map(toFbaTransportV2024Plan);
    },

    showPlan: async (planId: string): Promise<FbaTransportV2024Plan> => {
        const response = await migratedAxios.get<ShowPlanResponse>(`/api/fba-transport/v2024/plans/${planId}`);

        return toFbaTransportV2024Plan(response.data.data);
    },

    generatePackingOptions: async (planId: string): Promise<string> => {
        const body: GeneratePackingOptionsRequest = { plan_id: planId };
        const response = await migratedAxios.post<GeneratePackingOptionsResponse>(`/api/fba-transport/v2024/plans/${planId}/generate-packing-options`, body);

        return response.data;
    },

    fetchPackingOptions: async (planId: string): Promise<void> => {
        const query: FetchPackingOptionsQuery = { plan_id: planId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/packing-options`, location.origin);
        url.search = new URLSearchParams(query).toString();

        await migratedAxios.get(url.toString());
    },

    fetchPackingGroupItems: async (planId: string, packingOptionId: string, packingGroupId: string): Promise<void> => {
        const query: FetchPackingGroupItemsQuery = { plan_id: planId, packing_option_id: packingOptionId, packing_group_id: packingGroupId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/packing-group-items`, location.origin);
        url.search = new URLSearchParams(query).toString();

        await migratedAxios.get(url.toString());
    },

    confirmPackingOption: async (planId: string, packingOptionId: string): Promise<string> => {
        const body: ConfirmPackingOptionRequest = { plan_id: planId, packing_option_id: packingOptionId };
        const response = await migratedAxios.post<ConfirmPackingOptionResponse>(`/api/fba-transport/v2024/plans/${planId}/confirm-packing-option`, body);

        return response.data;
    },

    markPackingOptionAsConfirmed: async (planId: string, packingOptionId: string): Promise<void> => {
        const body: MarkPackingOptionAsConfirmedRequest = { plan_id: planId, packing_option_id: packingOptionId };
        await migratedAxios.post<MarkPackingOptionAsConfirmedResponse>(`/api/fba-transport/v2024/plans/${planId}/mark-packing-option-as-confirmed`, body);
    },

    createPlan: async (
        outboundShipmentId: number,
        channelId: number,
        items: FbaTransportV2024CreatePlanItem[],
        destinationMarketplace: string | null,
    ): Promise<FbaTransportV2024Plan> => {
        const body: CreatePlanRequest = {
            outbound_shipment_id: outboundShipmentId,
            channel_id: channelId,
            items: items.map(item => ({
                expiration: item.expiration,
                label_owner: item.labelOwner,
                msku: item.msku,
                prep_owner: item.prepOwner,
                quantity: item.quantity,
                manufacturing_lot_code: item.manufacturingLotCode,
            })),
            destination_marketplace: destinationMarketplace,
        };
        const response = await migratedAxios.post<CreatePlanResponse>('/api/fba-transport/v2024/plans', body);

        return toFbaTransportV2024Plan(response.data.data);
    },

    updatePlan: async (planId: string): Promise<void> => {
        await migratedAxios.post<UpdatePlanResponse>(`/api/fba-transport/v2024/plans/${planId}/update`);
    },

    getOperations: fetchPaginatedDataFunctionWithMapper<ListOperationsResponse['data'][number], FbaTransportV2024Operation>('/api/fba-transport/v2024/operations', toOperation, undefined, migratedAxios),

    showOperation: async (operationId: string): Promise<FbaTransportV2024Operation> => {
        const response = await migratedAxios.get<ShowOperationResponse>(`/api/fba-transport/v2024/operations/${operationId}`);

        return toOperation(response.data.data);
    },

    fetchOperationUpdate: async (operationId: string): Promise<FbaTransportV2024Operation> => {
        const response = await migratedAxios.get<FetchOperationUpdateResponse>(`/api/fba-transport/v2024/operations/${operationId}/update`);

        return toOperation(response.data.data);
    },

    generatePlacementOptions: async (planId: string): Promise<string> => {
        const body: GeneratePlacementOptionsRequest = { plan_id: planId };
        const response = await migratedAxios.post<GeneratePlacementOptionsResponse>(`/api/fba-transport/v2024/plans/${planId}/generate-placement-options`, body);

        return response.data;
    },

    fetchPlacementOptions: async (planId: string): Promise<void> => {
        await migratedAxios.get(`/api/fba-transport/v2024/plans/${planId}/placement-options`);
    },

    resetPlacementOptions: async (planId: string): Promise<void> => {
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/reset-placement-options`);
    },

    generateDeliveryWindowOptions: async (planId: string, shipmentId: string): Promise<string> => {
        const body: GenerateDeliveryWindowOptionsRequest = { plan_id: planId, shipment_id: shipmentId };
        const response = await migratedAxios.post<GenerateDeliveryWindowOptionsResponse>(`/api/fba-transport/v2024/plans/${planId}/generate-delivery-window-options`, body);

        return response.data;
    },

    fetchDeliveryWindowOptions: async (planId: string, placementOptionId: string, shipmentId: string): Promise<void> => {
        const query: FetchDeliveryWindowOptionsQuery = { plan_id: planId, placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/delivery-window-options`, location.origin);
        url.search = new URLSearchParams(query).toString();

        await migratedAxios.get(url.toString());
    },

    generateTransportationOptions: async (planId: string, placementOptionId: string, shipments: { shipmentId: string, startDate: string }[]): Promise<string> => {
        const body: GenerateTransportationOptionsRequest = {
            plan_id: planId,
            placement_option_id: placementOptionId,
            shipments_dates: shipments.map(({ shipmentId, startDate }) => ({ shipment_id: shipmentId, start_date: startDate })),
        };
        const response = await migratedAxios.post<GenerateTransportationOptionsResponse>(`/api/fba-transport/v2024/plans/${planId}/generate-transportation-options`, body);

        return response.data;
    },

    fetchTransportationOptions: async (planId: string, placementOptionId: string, shipmentId: string): Promise<void> => {
        const query: FetchTransportationOptionsQuery = { plan_id: planId, placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/transportation-options`, location.origin);
        url.search = new URLSearchParams(query).toString();

        await migratedAxios.get<FetchTransportationOptionsResponse>(url.toString());
    },

    createBoxGroup: async (planId: string, associatedEntityId: string, dimensions: TransportDimensions): Promise<FbaTransportBoxGroup> => {
        const body: CreateBoxGroupRequest = {
            associated_entity_id: associatedEntityId,
            length_mm: dimensions.lengthMm,
            width_mm: dimensions.widthMm,
            height_mm: dimensions.heightMm,
            weight_gm: dimensions.weightGm,
        };
        const response = await migratedAxios.post<CreateBoxGroupResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups`, body);

        return toBoxGroup(response.data.data);
    },

    updateBoxGroup: async (planId: string, boxGroupId: string, dimensions: TransportDimensions): Promise<void> => {
        const body: UpdateBoxGroupRequest = {
            length_mm: dimensions.lengthMm,
            width_mm: dimensions.widthMm,
            height_mm: dimensions.heightMm,
            weight_gm: dimensions.weightGm,
        };
        await migratedAxios.put<UpdateBoxGroupResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}`, body);
    },

    removeBoxGroup: async (planId: string, boxGroupId: string): Promise<void> => {
        await migratedAxios.delete<RemoveBoxGroupResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}`);
    },

    setBoxQuantityForBoxGroup: async (planId: string, boxGroupId: string, quantity: number): Promise<number[]> => {
        const body: SetBoxQuantityForBoxGroupRequest = { quantity };
        const response = await migratedAxios.post<SetBoxQuantityForBoxGroupResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}/set-box-quantity`, body);

        return response.data.box_numbers;
    },

    createBoxGroupItem: async (
        planId: string,
        boxGroupId: string,
        item: Pick<FbaTransportBoxGroupItem, 'planItemId' | 'quantity'>,
    ): Promise<FbaTransportBoxGroupItem> => {
        const body: CreateBoxGroupItemRequest = { plan_item_id: item.planItemId, quantity: item.quantity };
        const response = await migratedAxios.post<CreateBoxGroupItemResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}/items`, body);

        return toBoxGroupItem(response.data.box_group_item);
    },

    updateBoxGroupItem: async (
        planId: string,
        boxGroupId: string,
        itemId: string,
        quantity: number,
    ): Promise<FbaTransportBoxGroupItem> => {
        const body: UpdateBoxGroupItemRequest = { quantity };
        const response = await migratedAxios.put<UpdateBoxGroupItemResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}/items/${itemId}`, body);

        return toBoxGroupItem(response.data.box_group_item);
    },

    removeBoxGroupItem: async (planId: string, boxGroupId: string, itemId: string): Promise<void> => {
        await migratedAxios.delete<RemoveBoxGroupItemResponse>(`/api/fba-transport/v2024/plans/${planId}/box-groups/${boxGroupId}/items/${itemId}`);
    },

    createPallet: async (planId: string, placementOptionId: string, shipmentId: string, details: PalletDetails): Promise<void> => {
        const body: CreatePalletRequest = {
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            length_mm: details.lengthMm,
            width_mm: details.widthMm,
            height_mm: details.heightMm,
            weight_gm: details.weightGm,
            repeat: details.repeat,
            stackable: details.stackable,
        };
        await migratedAxios.post<CreatePalletResponse>(`/api/fba-transport/v2024/plans/${planId}/pallets`, body);
    },

    updatePallet: async (planId: string, placementOptionId: string, shipmentId: string, palletId: string, details: PalletDetails): Promise<void> => {
        const body: UpdatePalletRequest = {
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            length_mm: details.lengthMm,
            width_mm: details.widthMm,
            height_mm: details.heightMm,
            weight_gm: details.weightGm,
            repeat: details.repeat,
            stackable: details.stackable,
        };
        await migratedAxios.put<UpdatePalletResponse>(`/api/fba-transport/v2024/plans/${planId}/pallets/${palletId}`, body);
    },

    removePallet: async (planId: string, placementOptionId: string, shipmentId: string, palletId: string): Promise<void> => {
        const body: RemovePalletRequest = { placement_option_id: placementOptionId, shipment_id: shipmentId };
        await migratedAxios.delete<RemovePalletResponse>(`/api/fba-transport/v2024/plans/${planId}/pallets/${palletId}`, { data: body });
    },

    setNonPartneredLtlTracking: async (
        planId: string,
        shipmentId: string,
        billOfLadingNumber: string,
        freightBillNumber: string[],
    ): Promise<string> => {
        const body: SetNonPartneredLtlTrackingRequest = {
            plan_id: planId,
            shipment_id: shipmentId,
            bill_of_lading_number: billOfLadingNumber,
            freight_bill_number: freightBillNumber,
        };
        const response = await migratedAxios.post<SetNonPartneredLtlTrackingResponse>(`/api/fba-transport/v2024/plans/${planId}/update-tracking-ltl-details`, body);

        return response.data;
    },

    setNonPartneredSmallParcelTracking: async (
        planId: string,
        shipmentId: string,
        boxTrackingIds: { amazonBoxId: string, trackingId: string }[],
    ): Promise<string> => {
        const body: SetNonPartneredSmallParcelTrackingRequest = {
            plan_id: planId,
            shipment_id: shipmentId,
            box_tracking_ids: boxTrackingIds.map(({ amazonBoxId, trackingId }) => ({ amazon_box_id: amazonBoxId, tracking_id: trackingId })),
        };
        const response = await migratedAxios.post<SetNonPartneredSmallParcelTrackingResponse>(`/api/fba-transport/v2024/plans/${planId}/update-tracking-small-details`, body);

        return response.data;
    },

    updateContactInformation: async (planId: string, placementOptionId: string, shipmentId: string, details: FbaTransportV2024ShipmentContactInformation): Promise<void> => {
        const body: UpdateContactInformationRequest = {
            plan_id: planId,
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            email: details.email,
            name: details.name,
            phone_number: details.phoneNumber,
        };
        await migratedAxios.post<UpdateContactInformationResponse>(`/api/fba-transport/v2024/plans/${planId}/update-contact-information`, body);
    },

    updateFreightInformation: async (
        planId: string,
        placementOptionId: string,
        shipmentId: string,
        details: {
            freightClass?: FbaTransportV2024ShipmentFreightClass
            amount?: string
            code?: string
        },
    ): Promise<void> => {
        // A detail left undefined is omitted from the JSON body rather than sent as null.
        const body: UpdateFreightInformationRequest = {
            plan_id: planId,
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            freight_class: details.freightClass,
            amount: details.amount,
            code: details.code,
        };
        await migratedAxios.post<UpdateFreightInformationResponse>(`/api/fba-transport/v2024/plans/${planId}/update-freight-information`, body);
    },

    setFirstAvailabilityDate: async (planId: string, placementOptionId: string, shipmentId: string, firstAvailabilityDate: string): Promise<void> => {
        const body: SetFirstAvailabilityDateRequest = {
            plan_id: planId,
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            first_availability_date: firstAvailabilityDate,
        };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/set-first-availability-date`, body);
    },

    setTransportModePreference: async (planId: string, placementOptionId: string, shipmentId: string, transportModePreference: FbaTransportV2024ShipmentGroup): Promise<void> => {
        const body: SetTransportModePreferenceRequest = {
            plan_id: planId,
            placement_option_id: placementOptionId,
            shipment_id: shipmentId,
            transport_mode_preference: transportModePreference,
        };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/set-transport-mode-preference`, body);
    },

    getLabels: async (planId: string, shipmentId: string): Promise<FbaTransportV2024Label[]> => {
        const query: GetLabelsQuery = { shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/labels`, location.origin);
        url.search = new URLSearchParams(query).toString();

        const response = await migratedAxios.get<GetLabelsResponse>(url.toString());
        return response.data.data.map(toLabel);
    },

    requestPackageLabels: async (planId: string, shipmentId: string): Promise<void> => {
        const body: RequestPackageLabelsRequest = { shipment_id: shipmentId };
        await migratedAxios.post<RequestPackageLabelsResponse>(`/api/fba-transport/v2024/plans/${planId}/request-package-labels`, body);
    },

    requestAllBoxLabels: async (planId: string, shipmentId: string, boxGroupId: string | null = null, boxNumbers: number[] | null = null, sortByInternalNumber: boolean = false): Promise<void> => {
        const body: RequestAllBoxLabelsRequest = {
            shipment_id: shipmentId,
            box_group_id: boxGroupId,
            box_numbers: boxNumbers,
            sort_by_internal_number: sortByInternalNumber,
        };
        await migratedAxios.post<RequestAllBoxLabelsResponse>(`/api/fba-transport/v2024/plans/${planId}/request-box-labels`, body);
    },

    requestIndividualBoxLabels: async (planId: string, shipmentId: string, boxNumbers: number[], sortByInternalNumber: boolean = false): Promise<void> => {
        const body: RequestIndividualBoxLabelsRequest = {
            shipment_id: shipmentId,
            box_numbers: boxNumbers,
            sort_by_internal_number: sortByInternalNumber,
        };
        await migratedAxios.post<RequestIndividualBoxLabelsResponse>(`/api/fba-transport/v2024/plans/${planId}/request-individual-box-labels`, body);
    },

    requestPalletLabels: async (planId: string, shipmentId: string, pallets: { number: number }[]): Promise<void> => {
        const body: RequestPalletLabelsRequest = {
            shipment_id: shipmentId,
            pallets: pallets.map(({ number }) => ({ number })),
        };
        await migratedAxios.post<RequestPalletLabelsResponse>(`/api/fba-transport/v2024/plans/${planId}/request-pallet-labels`, body);
    },

    downloadPalletLabels: async (planId: string, shipmentId: string): Promise<void> => {
        const body: DownloadPalletLabelsRequest = { shipment_id: shipmentId };
        await migratedAxios.post<DownloadPalletLabelsResponse>(`/api/fba-transport/v2024/plans/${planId}/download-pallet-labels`, body);
    },

    requestBillOfLading: async (planId: string, shipmentId: string): Promise<void> => {
        const body: RequestBillOfLadingRequest = { shipment_id: shipmentId };
        await migratedAxios.post<RequestBillOfLadingResponse>(`/api/fba-transport/v2024/plans/${planId}/request-bill-of-lading`, body);
    },

    resizeToFourBySix: async (planId: string, shipmentId: string, labelId: string, sortByInternalNumber: boolean = false): Promise<void> => {
        const body: ResizeToFourBySixRequest = { shipment_id: shipmentId, sort_by_internal_number: sortByInternalNumber };
        await migratedAxios.post<ResizeToFourBySixResponse>(`/api/fba-transport/v2024/plans/${planId}/labels/${labelId}/resize-to-4x6`, body);
    },

    changeUsing2dBarcodes: async (planId: string, using2dBarcodes: boolean): Promise<void> => {
        const body: ChangeUsing2dBarcodesRequest = { using_2d_barcodes: using2dBarcodes };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/change-using-2d-barcodes`, body);
    },

    setPackingInformation: async (planId: string): Promise<string> => {
        const body: SetPackingInformationRequest = { plan_id: planId };
        const response = await migratedAxios.post<SetPackingInformationResponse>(`/api/fba-transport/v2024/plans/${planId}/set-packing-information`, body);

        return response.data;
    },

    markPackingInformationAsConfirmed: async (planId: string, packingGroupIds: string[]): Promise<void> => {
        const body: MarkPackingInformationAsConfirmedRequest = { packing_group_ids: packingGroupIds };
        await migratedAxios.post<MarkPackingInformationAsConfirmedResponse>(`/api/fba-transport/v2024/plans/${planId}/add-carton-request-to-submission`, body);
    },

    resetSubmissions: async (planId: string): Promise<ResetSubmissionsResponse> => {
        const response = await migratedAxios.post<ResetSubmissionsResponse>(`/api/fba-transport/v2024/plans/${planId}/reset-submissions`);
        return response.data;
    },

    selectPlacementOption: async (planId: string, placementOptionId: string): Promise<void> => {
        const body: SelectPlacementOptionRequest = { plan_id: planId, placement_option_id: placementOptionId };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/select-placement-option`, body);
    },

    confirmPlacementOption: async (planId: string, placementOptionId: string): Promise<string> => {
        const body: ConfirmPlacementOptionRequest = { plan_id: planId, placement_option_id: placementOptionId };
        const { data } = await migratedAxios.post<ConfirmPlacementOptionResponse>(`/api/fba-transport/v2024/plans/${planId}/confirm-placement-option`, body);
        return data;
    },

    markPlacementOptionAsConfirmed: async (planId: string, placementOptionId: string): Promise<MarkPlacementOptionAsConfirmedResponse> => {
        const body: MarkPlacementOptionAsConfirmedRequest = { plan_id: planId, placement_option_id: placementOptionId };
        const { data } = await migratedAxios.post<MarkPlacementOptionAsConfirmedResponse>(`/api/fba-transport/v2024/plans/${planId}/mark-placement-option-as-confirmed`, body);
        return data;
    },

    setBoxesIds: async (planId: string): Promise<void> => {
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/set-boxes-ids`, location.origin);
        await migratedAxios.get<SetBoxesIdsResponse>(url.toString());
    },

    updateShipmentName: async (planId: string, shipmentId: string, name: string): Promise<void> => {
        const body: UpdateShipmentNameRequest = { shipment_id: shipmentId, name };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/update-shipment-name`, body);
    },

    updatePackingInfoKnown: async (planId: string, isPackingInfoKnown: boolean): Promise<void> => {
        const body: UpdatePackingInfoKnownRequest = { is_packing_info_known: isPackingInfoKnown };
        await migratedAxios.post<void>(`/api/fba-transport/v2024/plans/${planId}/update-packing-info-known`, body);
    },

    confirmDeliveryWindowOption: async (planId: string, shipmentId: string, deliveryWindowOptionId: string): Promise<string> => {
        const body: ConfirmDeliveryWindowOptionRequest = { plan_id: planId, shipment_id: shipmentId, delivery_window_option_id: deliveryWindowOptionId };
        const { data } = await migratedAxios.post<ConfirmDeliveryWindowOptionResponse>(`/api/fba-transport/v2024/plans/${planId}/confirm-delivery-window-option`, body);
        return data;
    },

    markDeliveryWindowOptionAsConfirmed: async (planId: string, shipmentId: string, deliveryWindowOptionId: string): Promise<void> => {
        const body: MarkDeliveryWindowOptionAsConfirmedRequest = { plan_id: planId, shipment_id: shipmentId, delivery_window_option_id: deliveryWindowOptionId };
        await migratedAxios.post<MarkDeliveryWindowOptionAsConfirmedResponse>(`/api/fba-transport/v2024/plans/${planId}/mark-delivery-window-option-as-confirmed`, body);
    },

    confirmTransportationOption: async (planId: string, shipments: { shipmentId: string, transportationOptionId: string }[]): Promise<string> => {
        const body: ConfirmTransportationOptionRequest = {
            plan_id: planId,
            shipment_transportation_ids: shipments.map(({ shipmentId, transportationOptionId }) => ({ shipment_id: shipmentId, transportation_option_id: transportationOptionId })),
        };
        const { data } = await migratedAxios.post<ConfirmTransportationOptionResponse>(`/api/fba-transport/v2024/plans/${planId}/confirm-transportation-option`, body);
        return data;
    },

    markTransportationOptionsAsConfirmed: async (planId: string, shipments: { shipmentId: string, transportationOptionId: string }[]): Promise<void> => {
        const body: MarkTransportationOptionsAsConfirmedRequest = {
            plan_id: planId,
            shipment_transportation_ids: shipments.map(({ shipmentId, transportationOptionId }) => ({ shipment_id: shipmentId, transportation_option_id: transportationOptionId })),
        };
        await migratedAxios.post<MarkTransportationOptionsAsConfirmedResponse>(`/api/fba-transport/v2024/plans/${planId}/mark-transportation-option-as-confirmed`, body);
    },

    getShipments: async (planId: string, placementOptionId: string): Promise<FbaTransportV2024Shipment[]> => {
        const query: GetShipmentsQuery = { plan_id: planId, placement_option_id: placementOptionId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/get-shipment`, location.origin);
        url.search = new URLSearchParams(query).toString();

        const response = await migratedAxios.get<GetShipmentsResponse>(url.toString());

        return response.data.data.map(toShipment);
    },

    fetchShipmentBoxGroups: async (planId: string, placementOptionId: string, shipmentId: string): Promise<FbaTransportV2024ShipmentBoxGroupEntry[]> => {
        const query: FetchShipmentBoxGroupsQuery = { plan_id: planId, placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/list-shipment-boxes`, location.origin);
        url.search = new URLSearchParams(query).toString();

        const response = await migratedAxios.get<FetchShipmentBoxGroupsResponse>(url.toString());

        return response.data.data.map(toShipmentBoxGroup);
    },

    fetchShipmentItems: async (planId: string, placementOptionId: string, shipmentId: string): Promise<void> => {
        const query: FetchShipmentItemsQuery = { placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/list-shipment-items`, location.origin);
        url.search = new URLSearchParams(query).toString();

        await migratedAxios.get(url.toString());
    },

    cancelPlanChecker: async (planId: string): Promise<string[]> => {
        const body: CancelPlanCheckerRequest = { plan_id: planId };
        const { data } = await migratedAxios.post<CancelPlanCheckerResponse | ''>(`/api/fba-transport/v2024/plans/${planId}/cancel-plan-checker`, body);

        // The operation's 200 description ends "otherwise an empty body", which axios delivers as ''.
        return data === '' ? [] : data;
    },

    cancelPlan: async (planId: string): Promise<string> => {
        const body: CancelPlanRequest = { plan_id: planId };
        const { data } = await migratedAxios.post<CancelPlanResponse>(`/api/fba-transport/v2024/plans/${planId}/cancel-plan`, body);
        return data;
    },

    markPlanAsCanceled: async (planId: string): Promise<void> => {
        await migratedAxios.post<MarkPlanAsCanceledResponse>(`/api/fba-transport/v2024/plans/${planId}/mark-plan-as-canceled`);
    },

    getPickListUrl: async (planId: string, placementOptionId: string, shipmentId: string): Promise<string> => {
        const query: GetPickListQuery = { placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/pick-list`, location.origin);
        // The server ignores plan_id, which the operation does not document; it is still sent so the request stays as it was.
        url.search = new URLSearchParams({ plan_id: planId, ...query }).toString();

        const response = await migratedAxios.get<GetPickListResponse>(url.toString());

        return response.data.url;
    },

    getPackListUrl: async (planId: string, placementOptionId: string, shipmentId: string): Promise<string> => {
        const query: GetPackListQuery = { placement_option_id: placementOptionId, shipment_id: shipmentId };
        const url = new URL(`/api/fba-transport/v2024/plans/${planId}/pack-list`, location.origin);
        // The server ignores plan_id, which the operation does not document; it is still sent so the request stays as it was.
        url.search = new URLSearchParams({ plan_id: planId, ...query }).toString();

        const response = await migratedAxios.get<GetPackListResponse>(url.toString());

        return response.data.url;
    },

    listPlans: async (warehouseId: number, status: FbaTransportPlanStatus | null, paginationToken: string | null = null): Promise<ResponseListPlans> => {
        const url = new URL('/api/fba-transport/v2024/list-v2024-plans', location.origin);
        url.searchParams.append('warehouse_id', warehouseId.toString());
        if (status != null) {
            url.searchParams.append('status', status);
        }
        if (paginationToken != null && paginationToken !== '') {
            url.searchParams.append('pagination_token', paginationToken);
        }

        const response = await migratedAxios.get<ListAmazonPlansResponse>(url.toString());

        return {
            plans: response.data.plans.map(toImportedFbaPlan),
            paginationToken: response.data.pagination_token,
        };
    },

    importPlan: async (planId: string, channelId: number, warehouseId: number, createInbound: boolean = false): Promise<FbaTransportV2024Plan> => {
        const url = new URL('/api/fba-transport/v2024/import-plan', location.origin);
        url.searchParams.append('inbound_plan_id', planId);
        url.searchParams.append('channel_id', channelId.toString());
        url.searchParams.append('warehouse_id', warehouseId.toString());
        url.searchParams.append('create_inbound', createInbound ? '1' : '0');

        const response = await migratedAxios.get<ImportPlanResponse>(url.toString());

        return toFbaTransportV2024Plan(response.data.plan);
    },
});
